<template>
  <AccountContextWrapper>
    <template #default>
      <section class="budget-template-edit mt-3">
        <div class="text-start mb-3">
          <h2 class="h5 mb-2">Budget Templates</h2>
          <p class="mb-0 text-muted">
            Create and manage reusable budget templates that can seed future
            budget periods.
          </p>
        </div>

        <bPanel
          title="Edit Budget Template"
          tag="article"
          class="border"
          max-width="48rem"
        >
          <div v-if="loadingTemplate" class="text-center my-4">
            <BSpinner label="Loading template..." />
          </div>

          <div v-else-if="templateLoadError" class="alert alert-danger">
            {{ templateLoadError }}
          </div>

          <BForm v-else @submit="onSubmit">
            <BFormGroup
              label="Template Name"
              label-for="template-name"
              class="mb-3 floating-group"
            >
              <BFormInput
                id="template-name"
                v-model="form.name"
                placeholder="Enter template name"
                required
              />
            </BFormGroup>

            <BFormGroup
              label="Add Category"
              label-for="category-selector"
              class="mb-2 floating-group"
            >
              <div class="d-flex gap-2">
                <BFormSelect
                  id="category-selector"
                  v-model="selectedCategoryUid"
                  :options="availableCategoryOptions"
                  :disabled="
                    loadingCategories || availableCategoryOptions.length <= 1
                  "
                />
                <BButton
                  type="button"
                  variant="outline-secondary"
                  :disabled="!selectedCategoryUid"
                  @click="addCategory"
                >
                  Add
                </BButton>
              </div>
            </BFormGroup>

            <div v-if="loadingCategories" class="text-center my-4">
              <BSpinner label="Loading categories..." />
            </div>

            <div
              v-else-if="form.categories.length === 0"
              class="text-center my-4"
            >
              <IconAlertTriangle class="me-1 text-warning" />
              Add at least one category before saving.
            </div>

            <div v-else class="mb-3">
              <div
                v-for="(category, index) in form.categories"
                :key="`${category.categoryUid}-${index}`"
                class="border rounded p-2 mb-2"
              >
                <div class="row g-2 align-items-end">
                  <div class="col-md-6">
                    <BFormGroup
                      :label="`Category ${index + 1}`"
                      class="mb-0 floating-group"
                    >
                      <BFormInput :model-value="category.name" readonly />
                    </BFormGroup>
                  </div>
                  <div class="col-md-4">
                    <BFormGroup
                      label="Budget Amount"
                      class="mb-0 floating-group"
                    >
                      <BFormInput
                        :model-value="category.budgetAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        @update:modelValue="
                          updateCategoryAmount(category.categoryUid, $event)
                        "
                      />
                    </BFormGroup>
                  </div>
                  <div class="col-md-2">
                    <BButton
                      type="button"
                      variant="outline-danger"
                      class="w-100"
                      @click="removeCategory(category.categoryUid)"
                    >
                      Remove
                    </BButton>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger">
              {{ errorMessage }}
            </div>

            <div class="d-flex justify-content-end gap-2">
              <BButton
                type="button"
                variant="outline-secondary"
                @click="goBack"
              >
                Cancel
              </BButton>
              <BButton type="submit" variant="primary" :disabled="submitting">
                {{ submitting ? "Saving..." : "Save Changes" }}
              </BButton>
            </div>
          </BForm>
        </bPanel>
      </section>
    </template>
  </AccountContextWrapper>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BSpinner,
} from "bootstrap-vue-next";
import { IconAlertTriangle } from "@tabler/icons-vue";
import bPanel from "@/components/atoms/bPanel.vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import {
  budgetTemplates as budgetTemplatesNs,
  spendingCategory as spendingCategoryNs,
  userAccount as userAcctNs,
  userSession as userSessNs,
} from "@/store/namespaces";

const moldCategory = (category) => ({
  categoryUid: category.category_uid || category.categoryUid,
  name: category.name,
  budgetAmount: category.budget_amount ?? category.budgetAmount,
});

export default {
  name: "BudgetTemplatesEditView",
  components: {
    AccountContextWrapper,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BSpinner,
    IconAlertTriangle,
    bPanel,
  },
  data() {
    return {
      loadingTemplate: false,
      loadingCategories: false,
      submitting: false,
      templateLoadError: "",
      errorMessage: "",
      selectedCategoryUid: null,
      form: {
        name: "",
        categories: [],
      },
    };
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    templateUid() {
      return this.$route.params.templateUid;
    },
    templates() {
      return this.$store.getters[`${budgetTemplatesNs}/getTemplatesByAcctUid`](
        this.acctUid
      );
    },
    spendingCategories() {
      return (
        this.$store.getters[`${spendingCategoryNs}/getSpendingCategories`] || []
      );
    },
    availableCategoryOptions() {
      const pickedCategoryUids = new Set(
        this.form.categories.map((category) => category.categoryUid)
      );

      const options = this.spendingCategories
        .filter((category) => !pickedCategoryUids.has(category.item.uid))
        .map((category) => ({
          value: category.item.uid,
          text: category.item.name,
        }));

      return [{ value: null, text: "Select a category" }, ...options];
    },
  },
  watch: {
    acctUid: {
      immediate: true,
      async handler(nextAcctUid) {
        if (!nextAcctUid) {
          return;
        }

        await this.loadData(nextAcctUid);
      },
    },
  },
  methods: {
    async loadData(acctUid) {
      this.templateLoadError = "";
      this.loadingTemplate = true;

      try {
        await Promise.all([
          this.$store.dispatch(`${spendingCategoryNs}/fetchSpendingCategories`),
          this.$store.dispatch(
            `${budgetTemplatesNs}/fetchBudgetTemplatesByAcctUid`,
            {
              acctUid,
              force: true,
            }
          ),
        ]);

        const template = this.templates.find(
          (item) => item.template_uid === this.templateUid
        );

        if (!template) {
          this.templateLoadError = "The requested template could not be found.";
          return;
        }

        this.form = {
          name: template.name,
          categories: (template.categories || []).map(moldCategory),
        };
      } catch (error) {
        console.error("Error loading budget template:", error);
        this.templateLoadError = "Unable to load budget template details.";
      } finally {
        this.loadingTemplate = false;
        this.loadingCategories = false;
      }
    },
    addCategory() {
      if (!this.selectedCategoryUid) {
        return;
      }

      const selected = this.spendingCategories.find(
        (category) => category.item.uid === this.selectedCategoryUid
      )?.item;

      if (!selected) {
        return;
      }

      this.form.categories = [
        ...this.form.categories,
        {
          categoryUid: selected.uid,
          name: selected.name,
          budgetAmount: "",
        },
      ];
      this.selectedCategoryUid = null;
    },
    removeCategory(categoryUid) {
      this.form.categories = this.form.categories.filter(
        (category) => category.categoryUid !== categoryUid
      );
    },
    updateCategoryAmount(categoryUid, amount) {
      this.form.categories = this.form.categories.map((category) => {
        if (category.categoryUid !== categoryUid) {
          return category;
        }

        return {
          ...category,
          budgetAmount: amount,
        };
      });
    },
    getValidatedPayload() {
      const name = this.form.name.trim();
      if (!name) {
        return { error: "Template name is required." };
      }

      if (this.form.categories.length === 0) {
        return { error: "At least one category is required." };
      }

      const categories = this.form.categories.map((category) => ({
        categoryUid: category.categoryUid,
        name: category.name,
        budgetAmount: Number(category.budgetAmount),
      }));

      const hasInvalidAmounts = categories.some(
        (category) =>
          Number.isNaN(category.budgetAmount) || category.budgetAmount < 0
      );
      if (hasInvalidAmounts) {
        return {
          error:
            "Each category must have a budget amount greater than or equal to zero.",
        };
      }

      return {
        payload: {
          name,
          categories,
        },
      };
    },
    async onSubmit(event) {
      event.preventDefault();
      this.errorMessage = "";

      const validation = this.getValidatedPayload();
      if (validation.error) {
        this.errorMessage = validation.error;
        return;
      }

      try {
        this.submitting = true;
        const userInfo = await this.$store.dispatch(
          `${userSessNs}/getUserInfo`
        );
        await this.$store.dispatch(
          `${budgetTemplatesNs}/updateBudgetTemplate`,
          {
            templateUid: this.templateUid,
            acctUid: this.acctUid,
            userId: userInfo?.id,
            ...validation.payload,
          }
        );
        this.$notify.notifySuccess("Budget template updated successfully.");
        this.$router.push({ path: "/budget/templates" });
      } catch (error) {
        console.error("Error updating budget template:", error);
        this.errorMessage =
          "Unable to update budget template. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.push({ path: "/budget/templates" });
    },
  },
};
</script>

<style scoped>
.budget-template-edit {
  width: min(100%, 48rem);
}
</style>
